import {
	BaseState,
	FormsActionTypes,
	FormsEntities,
	MessageBox,
	Mobile,
	ProjectHelper,
	panelType
} from '@bimser/common';
import { BCNotification, findMessage } from '@bimser/components';
import { ViewerHelpers } from '@bimser/components/dist/BCLayoutCreator';
import * as LayoutCreatorEntities from '@bimser/components/dist/BCLayoutCreator/common/entities';
import {
	FormItemTypes,
	IBaseAction,
	IDictionary,
	RxEventEmitter,
	createGuid,
	createSyncAction,
	isNullOrUndefined
} from '@bimser/core';
import { all, call, put, select, take, takeEvery, takeLeading } from 'redux-saga/effects';
import { SetPanelActionFactoryFromJS } from '../entities';
import { ICheckRules } from '../entities/ICheckRules';
import { getTriggeredRules } from '../helper';
import { deepMergeFormItems, normalizeItemsForServer } from '../helper/deepMergeFormItems';
import { checkFormItemRules } from './ruleValidation/rule';

export default function* watchFormSaved() {
	yield all([
		takeLeading(FormsActionTypes.FORM_SAVED_SAGA, formSaved),
		takeEvery(FormsActionTypes.FORM_SAVE_TRANSACTION, formSaved)
	]);
}

export function* formSaved(sagaAction: IBaseAction<FormsEntities.FormSaveAction>) {
	const mainState: BaseState = yield select();
	const payload = sagaAction.payload.payload;
	const defaultLang = mainState.system.language.items.find(lang => lang.isDefault);

	const stopProcess = yield call(checkFormItemRules, {
		formData: payload.items,
		selectedCulture: mainState.user.selectedCulture,
		defaultCulture: defaultLang && defaultLang.culture,
		checkType: 'execution',
		windowId: sagaAction.payload.windowId,
		rules: payload.rules,
		validate: payload.validate
	} as ICheckRules);

	const viewersAndRepeaters = payload.items
		.filter(item =>
			[FormItemTypes.EControlTypes.FormViewer, FormItemTypes.EControlTypes.Repeater].includes(
				item.type as FormItemTypes.EControlTypes
			)
		)
		.values();

	let stopProcessViewerOrRepeater = false;
	for (const viewerOrRepeater of viewersAndRepeaters) {
		const result = yield call(checkFormViewerItemRules, viewerOrRepeater, payload.validate);
		if (!stopProcessViewerOrRepeater) {
			stopProcessViewerOrRepeater = result;
		}
	}

	if ((!stopProcess && !stopProcessViewerOrRepeater) || !payload.validate) {
		try {
			const response = yield call(SaveForm, payload.requestData);
			if (response) {
				const responseData: FormsEntities.IFormResponse = response.data;
				const items: LayoutCreatorEntities.INormalizedLayoutItems = responseData.form
					? responseData.form.data
					: null; // New form data
				const documentId: number = responseData.documentId; // Saved document id
				const formId: string = sagaAction.payload.windowId; // Panel id
				const lastBatchGuid = createGuid();
				const currentFormData = mainState.forms.get(sagaAction.payload.windowId);
				const mergedItems = deepMergeFormItems(
					currentFormData.items.toJS(),
					items,
					null,
					'Save'
				);

				const { structureItems, dataItems } =
					ViewerHelpers.splitDataAndStructureItems(mergedItems);

				let loadPayload = LayoutCreatorEntities.FormItemsLoadActionFactoryFromJS({
					items: mergedItems,
					dataItems:
						dataItems.toJS() as IDictionary<LayoutCreatorEntities.ILayoutDataItem>,
					structureItems: structureItems.toJS(),
					documentId,
					formId,
					lastBatchGuid,
					rules: null
				});

				loadPayload = loadPayload.set('rules', payload.rules);

				const updateSavedFormAction = createSyncAction(
					FormsActionTypes.FORM_ITEMS_LOADED_TRANSACTION,
					loadPayload
				);

				yield put(updateSavedFormAction);

				if (responseData.status === FormsEntities.IFormStatus.Completed) {
					RxEventEmitter.emit('external_' + sagaAction.payload.windowId, {
						type: 'FORM_SAVED',
						windowId: sagaAction.payload.windowId,
						response: responseData
					});
					Mobile.MobileView.mobileServiceResponse(
						true,
						'Save',
						JSON.stringify({
							status: 'Completed',
							formPanelId: sagaAction.payload.windowId
						})
					);

					if (!payload.disableMessages)
						BCNotification.success({
							message:
								payload.type === 'flow'
									? findMessage.get('102950')
									: findMessage.get('102951')
						});
				} else {
					RxEventEmitter.emit('external_' + sagaAction.payload.windowId, {
						type: 'FORM_SAVED_FAILED',
						windowId: sagaAction.payload.windowId,
						response: null
					});
					Mobile.MobileView.mobileServiceResponse(
						false,
						'Save',
						JSON.stringify({
							status: 'Failed',
							formPanelId: sagaAction.payload.windowId
						})
					);
					const message: any = {
						typeName: 'ShowMessageAction',
						dialogType: 'Toaster',
						alertType: 'Error',
						id: createGuid(),
						messages: findMessage.get('102597'),
						name: findMessage.get('100926'),
						enabled: true
					};
					Mobile.MobileView.mobileClientSideMessages([message]);
				}

				const action = yield take([FormsActionTypes.LAST_BATCH_GUID_RESOLVED]);
				if (action && action.payload && action.payload === lastBatchGuid) {
					if (payload.onAfterSave) {
						payload.onAfterSave(responseData.status, responseData.secretKey);
					}
				}

				if (responseData.status === FormsEntities.IFormStatus.Canceled) {
					BCNotification.success({
						message: findMessage.get('100742'),
						description: findMessage.get('101549')
					}); // Form kaydetme işlemi iptal edildi
					Mobile.MobileView.mobileServiceResponse(
						false,
						'Save',
						JSON.stringify({
							status: 'Canceled',
							formPanelId: sagaAction.payload.windowId
						})
					);
				}

				const [panelId] = formId.split('_');
				let panelData = yield select(getFlowViewerPanelData(panelId));
				if (panelData) {
					const documentIndex = panelData.documents?.findIndex(
						(d: { createOptions: { formName: string; projectName: string } }) =>
							d.createOptions.formName === payload.requestData.formName &&
							d.createOptions.projectName === payload.requestData.projectName
					);
					if (documentIndex && documentIndex !== -1) {
						panelData = panelData.setIn(
							['documents', documentIndex, 'documentId'],
							documentId
						);

						yield put(
							createSyncAction(
								FormsActionTypes.SET_PANEL_CONTENT_ACTION,
								SetPanelActionFactoryFromJS({
									panelId,
									panelType: panelType.flowViewer,
									data: panelData
								})
							)
						);
					}
				}
			}
		} catch (error) {
			MessageBox.error({ error: (error.exception as any)?.data });
			RxEventEmitter.emit('external_' + sagaAction.payload.windowId, {
				type: 'FORM_SAVED_FAILED',
				windowId: sagaAction.payload.windowId,
				response: null
			});
			Mobile.MobileView.mobileServiceResponse(
				false,
				'Save',
				JSON.stringify({ status: 'Failed', formPanelId: sagaAction.payload.windowId })
			);
		}
	} else {
		RxEventEmitter.emit('external_' + sagaAction.payload.windowId, {
			type: 'FORM_SAVED_FAILED',
			windowId: sagaAction.payload.windowId,
			response: null
		});
	}
}

export function* checkFormViewerItemRules(
	viewerOrRepeater: LayoutCreatorEntities.LayoutItem,
	validate: boolean
) {
	let stopProcess = false;

	if (viewerOrRepeater) {
		const mainState: BaseState = yield select();
		const formsState = mainState.forms
			.filter((_, key) => key.includes(viewerOrRepeater.id))
			.values();

		let index = 0;

		for (const formState of formsState) {
			const form = formState.items.entities.items.find(
				item => item.type === FormItemTypes.EContainerTypes.Form
			);

			const windowId = mainState.forms.findKey((_, key) => {
				if (viewerOrRepeater.type === FormItemTypes.EControlTypes.Repeater) {
					const data = viewerOrRepeater.properties.get('documentsData')[index];

					if (data) {
						const id = data.id;

						if (isNullOrUndefined(id)) {
							return (
								key.includes(viewerOrRepeater.id) &&
								key.split('_').pop() === data.documentId.toString()
							);
						}

						return key.includes(viewerOrRepeater.id) && key.includes(id);
					}
				}

				return key.includes(viewerOrRepeater.id);
			});

			if (windowId) {
				const result = yield call(checkFormItemRules, {
					formData: formState.items.entities.items,
					checkType: 'execution',
					rules: getTriggeredRules(form.properties.get('rules'), {
						controlId: form.id,
						eventName: 'OnToolbarButtonClicked'
					}),
					windowId,
					validate
				});

				if (!stopProcess) {
					stopProcess = result;
				}
			}

			index++;
		}
	}

	return stopProcess;
}

export function SaveForm(data: FormsEntities.FormSaveRequestData) {
	const formData: LayoutCreatorEntities.INormalizedLayoutItems = removeFormRules(
		data.formData
	).toJS();
	return ProjectHelper.RunPromiseFormService({
		projectName: data.projectName,
		formName: data.formName,
		serviceName: 'Save',
		requestParameters: {
			secretKey: data.secretKey,
			form: { data: normalizeItemsForServer(formData) }
		}
	});
}

const getFlowViewerPanelData = (panelId: string) => state =>
	state.modules.workflow.panels.flowViewerForms.get(panelId);

function removeFormRules(
	data: LayoutCreatorEntities.NormalizedLayoutItems
): LayoutCreatorEntities.NormalizedLayoutItems {
	const formItem = data.entities.items.find(
		item => item.type === FormItemTypes.EContainerTypes.Form
	);
	return data.setIn(['entities', 'items', formItem.id, 'properties', 'rules'], {});
}
